import React, { useEffect, useState } from "react";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/FuturePurchaseSlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import {
  useChangeStatusFuturePurchaseByIdMutation,
  useDeleteFuturePurchaseByIdMutation,
  useGetAllFuturePurchaseDataQuery,
  useGetAllFuturePurchaseExcelDataMutation,
} from "src/services/FuturePurchaseService";
import moment from "moment";
import { RxPencil1 } from "react-icons/rx";
import { MdDeleteOutline } from "react-icons/md";
import { TiEyeOutline } from "react-icons/ti";

import { Tooltip } from "@mui/material";
import AddFuturePurchaseWrapper from "../Add/AddFuturePurchaseWrapper";
import FuturePurchaseListing from "./FuturePurchaseListing";
import EditStatusWrapper from "../Edit/EditStatusWrapper";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const FuturePurchaseListingWrapper = (props: Props) => {
  const dispatch = useDispatch();

  const {
    items,
    totalItems,
    page,
    isOpenAddDialog,
    rowsPerPage,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
    dateFilter,
  } = useSelector((state: RootState) => state.futurePurchase);
  const [deleteFuturePurchase] = useDeleteFuturePurchaseByIdMutation();
  const [changeStatusFuturePurchase] =
    useChangeStatusFuturePurchaseByIdMutation();
  const [selectedFuturePurchaseId, setSelectedFuturePurchaseId] =
    useState<string>();
  const { data, isLoading, isFetching } = useGetAllFuturePurchaseDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["name", "mobile"],
    page: page,
    filterBy: [],
    dateFilter: {
      startDate: dateFilter.start_date
        ? moment(dateFilter?.start_date).format("YYYY-MM-DD")
        : "",
      endDate: dateFilter.end_date
        ? moment(dateFilter?.end_date).format("YYYY-MM-DD")
        : dateFilter.start_date
        ? moment().format("YYYY-MM-DD")
        : "",
    },
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const moduleName = "FUTURE_PURCHASE";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const getActionOptions = (row: any) => {
    const options = [];

    if (canEdit) {
      options.push({
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          dispatch(setIsOpenEditDialog(true));
          setSelectedFuturePurchaseId(row?._id);
        },
      });
    }

    if (canActive) {
      options.push({
        label: (
          <div className="flex gap-2 items-center font-semibold">
            <TiEyeOutline className="text-lg" /> Active/Deactive
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to change status of this FuturePurchase?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                changeStatusFuturePurchase({ id: row?._id }).then(
                  (res: any) => {
                    if (res?.error) {
                      showToast("error", res?.error?.data?.message);
                    } else {
                      if (res?.data?.status) {
                        showToast("success", "Status updated successfully");
                      } else {
                        showToast("error", res?.data?.message);
                      }
                    }
                  }
                );
              }
            },
          });
        },
      });
    }

    if (canDelete) {
      options.push({
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to delete this FuturePurchase?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteFuturePurchase(row?._id).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", "Deleted successfully");
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
      });
    }

    return options;
  };

  const columns: columnTypes[] = [
    {
      field: "purchaseDate",
      headerName: "Date",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <Tooltip title={row?.name || "-"}>
          <div className="capitalize text-[13px]"> {row?.name || "-"} </div>
        </Tooltip>
      ),
    },
    {
      field: "mobile",
      headerName: "Mobile Number",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "status",
      headerName: "Current Status",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <Tooltip title={row?.status || "-"}>
          <div className="text-[13px]">
            {row?.status
              ? row.status.charAt(0).toUpperCase() +
                row.status.slice(1).toLowerCase()
              : "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  const [getFuturePurchaseExcel, getFuturePurchaseExcelInfo] =
    useGetAllFuturePurchaseExcelDataMutation();
  // useEffect(() => {
  //   if (!getFuturePurchaseExcelInfo?.isLoading) {
  //     setFuturePurchaseExcel(getFuturePurchaseExcelInfo?.data?.data || []);
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [getFuturePurchaseExcelInfo?.isLoading]);

  return (
    <>
      <div>
        <FuturePurchaseListing
          // setFuturePurchaseExcel={setFuturePurchaseExcel}
          //   FuturePurchaseExceldata={FuturePurchaseExceldata}
          isloadingExcel={getFuturePurchaseExcelInfo.isLoading}
          getFuturePurchaseExcel={getFuturePurchaseExcel}
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setPage: (newPage) => dispatch(setPage(newPage)),
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddFuturePurchaseWrapper
          onClose={() => dispatch(setIsOpenAddDialog(false))}
        />
      ) : null}
      {isOpenEditDialog ? (
        <EditStatusWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedFuturePurchaseId={selectedFuturePurchaseId || ""}
        />
      ) : null}
    </>
  );
};

export default FuturePurchaseListingWrapper;
